import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useData } from '../../../contexts/DataContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useEmail } from '../../../contexts/EmailContext'; // NY
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiSend, FiHelpCircle, FiMessageSquare, FiAlertCircle, FiCheckCircle, FiMail, FiPhone } = FiIcons;

const SupportPanel = () => {
  const { user } = useAuth();
  const { submitSupportTicket } = useData();
  const { sendSupportReceipt } = useEmail(); // NY
  
  const [formData, setFormData] = useState({
    subject: '',
    category: 'general',
    message: '',
    priority: 'medium'
  });
  const [status, setStatus] = useState('idle'); // idle, submitting, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    try {
      if (!user) throw new Error('Du må være logget inn for å sende forespørsel.');
      
      // 1. Lagre ticket i systemet
      await submitSupportTicket({
        ...formData,
        user_email: user.email,
        user_id: user.id || 'unknown',
        company_id: user.companyId || 'unknown'
      });

      // 2. Send e-post bekreftelse (NY)
      await sendSupportReceipt(user.email, formData);

      setStatus('success');
      setFormData({
        subject: '',
        category: 'general',
        message: '',
        priority: 'medium'
      });
    } catch (error) {
      console.error('Support submission error:', error);
      setStatus('error');
      setErrorMessage('Det oppstod en feil ved innsending. Vennligst prøv igjen senere.');
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-8 text-white shadow-lg">
        <div className="flex items-center space-x-4">
          <div className="p-3 bg-white/10 rounded-lg">
            <SafeIcon icon={FiHelpCircle} className="text-white text-3xl" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Kundesupport</h2>
            <p className="text-blue-100 mt-1">Vi er her for å hjelpe deg. Send oss en melding direkte fra dashboardet.</p>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden transition-colors">
            <div className="p-6">
              {status === 'success' ? (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                    <SafeIcon icon={FiCheckCircle} className="text-green-600 dark:text-green-400 text-4xl" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Takk for din henvendelse!</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-8">
                    Vi har mottatt din melding og vil behandle den så snart som mulig. Du vil motta en bekreftelse på e-post ({user?.email}).
                  </p>
                  <button 
                    onClick={() => setStatus('idle')}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Send ny melding
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {status === 'error' && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg flex items-center">
                      <SafeIcon icon={FiAlertCircle} className="mr-2" />
                      {errorMessage}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Kategori
                      </label>
                      <div className="relative">
                        <select
                          name="category"
                          value={formData.category}
                          onChange={handleChange}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none transition-colors"
                        >
                          <option value="general">Generelt spørsmål</option>
                          <option value="technical">Teknisk problem</option>
                          <option value="billing">Faktura og abonnement</option>
                          <option value="hms">HMS og Varsling</option>
                          <option value="feature">Ønske om funksjonalitet</option>
                        </select>
                        <SafeIcon icon={FiMessageSquare} className="absolute left-3 top-3 text-gray-400 dark:text-gray-500" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Prioritet
                      </label>
                      <select
                        name="priority"
                        value={formData.priority}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      >
                        <option value="low">Lav</option>
                        <option value="medium">Normal</option>
                        <option value="high">Høy</option>
                        <option value="critical">Kritisk</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Emne *
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="Kort beskrivelse av henvendelsen"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Melding *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="6"
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="Beskriv problemet eller spørsmålet ditt så detaljert som mulig..."
                    />
                  </div>

                  <div className="flex items-center justify-end pt-4">
                    <button
                      type="submit"
                      disabled={status === 'submitting'}
                      className={`flex items-center space-x-2 bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-all shadow-md hover:shadow-lg ${status === 'submitting' ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {status === 'submitting' ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          <span>Sender...</span>
                        </>
                      ) : (
                        <>
                          <SafeIcon icon={FiSend} />
                          <span>Send henvendelse</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 transition-colors">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Kontaktinfo</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg">
                  <SafeIcon icon={FiMail} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">E-post</p>
                  <a href="mailto:support@hmsvarsling.no" className="text-sm text-blue-600 dark:text-blue-400 hover:underline">support@hmsvarsling.no</a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg">
                  <SafeIcon icon={FiPhone} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Telefon</p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">40 55 19 89</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Hverdager 08:00 - 16:00</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl shadow-sm border border-blue-100 dark:border-blue-800 p-6 transition-colors">
            <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Driftsmeldinger</h3>
            <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400 mb-2">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              Alle systemer fungerer normalt
            </div>
            <p className="text-xs text-blue-700 dark:text-blue-300">
              Ingen planlagte nedetider den nærmeste uken.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportPanel;